<template>
  <TextFieldWithValidation
    ref="field"
    v-model="password"
    :append-inner-icon="icons[showPassword ? 'mdiEyeOutline' : 'mdiEyeOffOutline']"
    :type="showPassword ? 'text' : 'password'"
    :color="strengthClass"
    :label="label"
    :suffix="translatedComplexity"
    :rules="rules"
    :name="name"
    :trim="false"
    required
    @click:append-inner="showPassword = !showPassword"
  />
</template>

<script setup lang="ts">
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import { PasswordComplexity, calculatePasswordStrength } from '~/utils/validation/password'
import {defineRule} from "vee-validate";

const icons = { mdiEyeOutline, mdiEyeOffOutline }
const password = ref('')
const passwordComplexity = ref(PasswordComplexity.Weak)
const translatedComplexity = ref('')
const showPassword = ref(false)
const field = useTemplateRef('field')
const i18n = useI18n()

const theme = useTheme()
const strengthClass = ref(theme.current.value.colors.primary)

const model = defineModel<string>()

const props = defineProps<{
    alternateLabel?: string,
    name: string,
    alternateRules?: string
}>()

const label = computed(() => {
  return props.alternateLabel ?? i18n.t('label.password')
})
const rules = computed(() => {
  return props.alternateRules ?? 'required|min:8|max:36|su_complexity'
})

watch(password, (val) => {
  model.value = val
  if (!props.alternateRules) {
    passwordComplexity.value = calculatePasswordStrength(val)
    if (passwordComplexity.value === PasswordComplexity.Strong) {
      strengthClass.value = theme.current.value.colors.success
    } else if (passwordComplexity.value === PasswordComplexity.Good) {
      strengthClass.value = theme.current.value.colors.warning
    } else {
      strengthClass.value = theme.current.value.colors.error
    }
    translatedComplexity.value = i18n.ts(`password.label.${PasswordComplexity[passwordComplexity.value].toLowerCase()}`)
  }
})

defineRule('su_complexity',   (value: string) => {
  passwordComplexity.value = calculatePasswordStrength(value)
  if (passwordComplexity.value !== PasswordComplexity.Weak) {
    return true
  }
  return i18n.t('fieldname.weak') as string
})

/** Moves keyboard focus to the input. */
const focus = () => field.value?.focus()
defineExpose({ focus })


</script>
